import { clamp, mat3TransformPoint, type Mat3 } from '../core/math';
import type { Point, Rect } from '../core/types';
import { knot, type Knot, type ShapeGeometry, type Subpath } from './types';

/** Flatness tolerance used when a caller does not care to tune it, in pixels. */
const DEFAULT_TOLERANCE = 0.25;

/** Recursion cap for adaptive subdivision; degenerate curves bottom out here. */
const MAX_SUBDIVISION_DEPTH = 24;

/** Bezier-to-circle handle length as a fraction of the radius. */
const KAPPA = 0.5522847498;

function mid(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Perpendicular distance from `p` to the *infinite* line through `a` and `b`. */
function distanceToLine(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  const cross = (p.x - a.x) * dy - (p.y - a.y) * dx;
  return Math.abs(cross) / Math.sqrt(lengthSq);
}

/** Distance from `p` to the finite segment `a`-`b` (clamped projection). */
function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq, 0, 1);
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Recursive de Casteljau subdivision. Appends every point of the curve after
 * `p0` (so callers that already hold `p0` as their last point can call this
 * repeatedly without duplicating it). A cubic is "flat enough" once both
 * control points sit within `tolerance` of the chord, which is the standard
 * flatness test for adaptive Bezier tessellation.
 */
function flattenCubicInto(
  points: Point[],
  p0: Point,
  p1: Point,
  p2: Point,
  p3: Point,
  tolerance: number,
  depth: number,
): void {
  const flat =
    depth >= MAX_SUBDIVISION_DEPTH ||
    (distanceToLine(p1, p0, p3) <= tolerance && distanceToLine(p2, p0, p3) <= tolerance);
  if (flat) {
    points.push({ x: p3.x, y: p3.y });
    return;
  }
  const p01 = mid(p0, p1);
  const p12 = mid(p1, p2);
  const p23 = mid(p2, p3);
  const p012 = mid(p01, p12);
  const p123 = mid(p12, p23);
  const p0123 = mid(p012, p123);
  flattenCubicInto(points, p0, p01, p012, p0123, tolerance, depth + 1);
  flattenCubicInto(points, p0123, p123, p23, p3, tolerance, depth + 1);
}

/** Appends the flattened curve from knot `a` to knot `b` onto `points`. */
function appendSegment(points: Point[], a: Knot, b: Knot, tolerance: number): void {
  const p0 = a.point;
  const p1: Point = { x: a.point.x + a.out.x, y: a.point.y + a.out.y };
  const p2: Point = { x: b.point.x + b.in.x, y: b.point.y + b.in.y };
  const p3 = b.point;
  flattenCubicInto(points, p0, p1, p2, p3, tolerance, 0);
}

/**
 * Flattens a subpath's cubic segments into a polyline, subdividing each one
 * against a flatness `tolerance` in pixels. An empty subpath yields an empty
 * array and a single-knot subpath yields that one point untouched (there is
 * no segment to flatten). A closed subpath's final edge — from the last knot
 * back to the first — is flattened too, but its own end point is dropped
 * since it is identical to the polyline's first point; the caller is
 * expected to treat the polyline as implicitly closed rather than find a
 * duplicate point there.
 */
export function subpathToPolyline(subpath: Subpath, tolerance = DEFAULT_TOLERANCE): Point[] {
  const { knots } = subpath;
  if (knots.length === 0) return [];
  if (knots.length === 1) return [{ x: knots[0].point.x, y: knots[0].point.y }];

  const points: Point[] = [{ x: knots[0].point.x, y: knots[0].point.y }];
  for (let i = 0; i < knots.length - 1; i += 1) {
    appendSegment(points, knots[i], knots[i + 1], tolerance);
  }
  if (subpath.closed) {
    appendSegment(points, knots[knots.length - 1], knots[0], tolerance);
    points.pop();
  }
  return points;
}

export function pathToPolylines(
  subpaths: readonly Subpath[],
  tolerance = DEFAULT_TOLERANCE,
): Point[][] {
  return subpaths.map((subpath) => subpathToPolyline(subpath, tolerance));
}

/**
 * Tight bounds of a path, measured on its flattened geometry rather than its
 * control points — a curve's control points can sit well outside the curve
 * itself, so a control-point box would bulge past where the pixels actually
 * fall. Returns null when there are no points at all (no subpaths, or every
 * subpath empty).
 */
export function pathBounds(subpaths: readonly Subpath[]): Rect | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;

  for (const polyline of pathToPolylines(subpaths)) {
    for (const p of polyline) {
      found = true;
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }

  if (!found) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Applies the matrix's linear part only — no translation — to a handle offset. */
function transformOffset(matrix: Mat3, offset: Point): Point {
  return {
    x: matrix[0] * offset.x + matrix[3] * offset.y,
    y: matrix[1] * offset.x + matrix[4] * offset.y,
  };
}

/**
 * Transforms every anchor and handle by `matrix`. Anchors are positions, so
 * they take the full affine transform; handles are offsets *relative to
 * their anchor*, so they take only the matrix's linear (rotation/scale/skew)
 * part — translating them too would shift the handle away from its anchor
 * instead of carrying it along.
 */
export function transformSubpaths(subpaths: readonly Subpath[], matrix: Mat3): Subpath[] {
  return subpaths.map((subpath) => ({
    ...subpath,
    knots: subpath.knots.map((k) => ({
      point: mat3TransformPoint(matrix, k.point.x, k.point.y),
      in: transformOffset(matrix, k.in),
      out: transformOffset(matrix, k.out),
      smooth: k.smooth,
    })),
  }));
}

/** Cubic control quadruple: two anchors with the two handles between them. */
type Cubic = [Point, Point, Point, Point];

function lerp(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** Splits a cubic at `t`, exactly, into the two cubics that replace it. */
function splitAt(cubic: Cubic, t: number): [Cubic, Cubic] {
  const [p0, p1, p2, p3] = cubic;
  const p01 = lerp(p0, p1, t);
  const p12 = lerp(p1, p2, t);
  const p23 = lerp(p2, p3, t);
  const p012 = lerp(p01, p12, t);
  const p123 = lerp(p12, p23, t);
  const p0123 = lerp(p012, p123, t);
  return [
    [p0, p01, p012, p0123],
    [p0123, p123, p23, p3],
  ];
}

/** The same curve as `pieces` equal-parameter cubics. */
function splitEvenly(cubic: Cubic, pieces: number): Cubic[] {
  const out: Cubic[] = [];
  let rest = cubic;
  for (let i = pieces; i > 1; i -= 1) {
    const [head, tail] = splitAt(rest, 1 / i);
    out.push(head);
    rest = tail;
  }
  out.push(rest);
  return out;
}

function difference(from: Point, to: Point): Point {
  return { x: to.x - from.x, y: to.y - from.y };
}

/** Rebuilds knots from a chain of cubics where each one starts where the last ended. */
function knotsFromCubics(cubics: readonly Cubic[], closed: boolean): Knot[] {
  const knots: Knot[] = [];
  for (let i = 0; i < cubics.length; i += 1) {
    const [point, outHandle] = cubics[i];
    const previous = i > 0 ? cubics[i - 1] : closed ? cubics[cubics.length - 1] : null;
    knots.push({
      point,
      in: previous ? difference(point, previous[2]) : ZERO,
      out: difference(point, outHandle),
      smooth: previous != null,
    });
  }
  if (!closed) {
    const tail = cubics[cubics.length - 1];
    knots.push({ point: tail[3], in: difference(tail[3], tail[2]), out: ZERO, smooth: false });
  }
  return knots;
}

/**
 * Bends paths through an arbitrary point map, which is how Warp reaches a shape
 * layer. A map that is not affine cannot be expressed by moving the existing
 * anchors, so every segment is first cut into `pieces` cubics: the curve then has
 * enough anchors to follow the bend, and stays a curve instead of being traced
 * into a polygon.
 */
export function deformSubpaths(
  subpaths: readonly Subpath[],
  map: (point: Point) => Point,
  pieces = 6,
): Subpath[] {
  return subpaths.map((subpath) => {
    const { knots, closed } = subpath;
    if (knots.length === 0) return { ...subpath, knots: [] };
    if (knots.length === 1) return { ...subpath, knots: [knot(map(knots[0].point))] };

    const cubics: Cubic[] = [];
    const segments = closed ? knots.length : knots.length - 1;
    for (let i = 0; i < segments; i += 1) {
      const a = knots[i];
      const b = knots[(i + 1) % knots.length];
      const segment: Cubic = [
        a.point,
        { x: a.point.x + a.out.x, y: a.point.y + a.out.y },
        { x: b.point.x + b.in.x, y: b.point.y + b.in.y },
        b.point,
      ];
      for (const piece of splitEvenly(segment, Math.max(1, Math.round(pieces)))) {
        cubics.push([map(piece[0]), map(piece[1]), map(piece[2]), map(piece[3])]);
      }
    }
    return { ...subpath, knots: knotsFromCubics(cubics, closed) };
  });
}

/** A corner knot with no handles. */
function corner(point: Point): Knot {
  return knot(point);
}

/** A knot with an explicit (possibly asymmetric) pair of handle offsets. */
function withHandles(point: Point, inHandle: Point, outHandle: Point): Knot {
  return { point, in: inHandle, out: outHandle, smooth: true };
}

const ZERO: Point = { x: 0, y: 0 };

function rectangleSubpath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): Subpath {
  const r = clamp(radius, 0, Math.min(width, height) / 2);
  if (r <= 0) {
    return {
      knots: [
        corner({ x, y }),
        corner({ x: x + width, y }),
        corner({ x: x + width, y: y + height }),
        corner({ x, y: y + height }),
      ],
      closed: true,
      op: 'add',
    };
  }

  const k = r * KAPPA;
  // Eight knots, one pair per corner: the arc-entry point carries the
  // in-handle, the arc-exit point carries the out-handle, and the straight
  // edge between two corners' knots is left with a zero handle on that side.
  const knots: Knot[] = [
    withHandles({ x: x + r, y }, { x: -k, y: 0 }, ZERO),
    withHandles({ x: x + width - r, y }, ZERO, { x: k, y: 0 }),
    withHandles({ x: x + width, y: y + r }, { x: 0, y: -k }, ZERO),
    withHandles({ x: x + width, y: y + height - r }, ZERO, { x: 0, y: k }),
    withHandles({ x: x + width - r, y: y + height }, { x: k, y: 0 }, ZERO),
    withHandles({ x: x + r, y: y + height }, ZERO, { x: -k, y: 0 }),
    withHandles({ x, y: y + height - r }, { x: 0, y: k }, ZERO),
    withHandles({ x, y: y + r }, ZERO, { x: 0, y: -k }),
  ];
  return { knots, closed: true, op: 'add' };
}

function ellipseSubpath(x: number, y: number, width: number, height: number): Subpath {
  const rx = width / 2;
  const ry = height / 2;
  const cx = x + rx;
  const cy = y + ry;
  const kx = rx * KAPPA;
  const ky = ry * KAPPA;
  const knots: Knot[] = [
    withHandles({ x: cx, y }, { x: -kx, y: 0 }, { x: kx, y: 0 }),
    withHandles({ x: x + width, y: cy }, { x: 0, y: -ky }, { x: 0, y: ky }),
    withHandles({ x: cx, y: y + height }, { x: kx, y: 0 }, { x: -kx, y: 0 }),
    withHandles({ x, y: cy }, { x: 0, y: ky }, { x: 0, y: -ky }),
  ];
  return { knots, closed: true, op: 'add' };
}

function lineSubpath(from: Point, to: Point): Subpath {
  return { knots: [corner(from), corner(to)], closed: false, op: 'add' };
}

function polygonSubpath(
  cx: number,
  cy: number,
  radius: number,
  sides: number,
  innerRatio: number,
  rotation: number,
): Subpath {
  const isStar = innerRatio < 1;
  const count = isStar ? sides * 2 : sides;
  const step = isStar ? Math.PI / sides : (2 * Math.PI) / sides;
  const knots: Knot[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = rotation + i * step;
    const r = isStar && i % 2 === 1 ? radius * innerRatio : radius;
    knots.push(corner({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }));
  }
  return { knots, closed: true, op: 'add' };
}

/**
 * Derives the editable knots behind a parametric shape. The result is plain
 * geometry — callers that need to keep editing it as a freeform path store
 * the result back as a `{ kind: 'freeform' }` geometry rather than mutating
 * this output in place.
 */
export function geometryToSubpaths(geometry: ShapeGeometry): Subpath[] {
  switch (geometry.kind) {
    case 'freeform':
      return geometry.subpaths;
    case 'rectangle':
      return [
        rectangleSubpath(geometry.x, geometry.y, geometry.width, geometry.height, geometry.radius),
      ];
    case 'ellipse':
      return [ellipseSubpath(geometry.x, geometry.y, geometry.width, geometry.height)];
    case 'line':
      return [lineSubpath(geometry.from, geometry.to)];
    case 'polygon':
      return [
        polygonSubpath(
          geometry.cx,
          geometry.cy,
          geometry.radius,
          geometry.sides,
          geometry.innerRatio,
          geometry.rotation,
        ),
      ];
  }
}

/** True when `at` lies within `tolerance` of any flattened edge of `subpaths`. */
export function hitTestPath(subpaths: readonly Subpath[], at: Point, tolerance: number): boolean {
  for (const subpath of subpaths) {
    const polyline = subpathToPolyline(subpath);
    if (polyline.length < 2) continue;
    const edgeCount = subpath.closed ? polyline.length : polyline.length - 1;
    for (let i = 0; i < edgeCount; i += 1) {
      const a = polyline[i];
      const b = polyline[(i + 1) % polyline.length];
      if (distanceToSegment(at, a, b) <= tolerance) return true;
    }
  }
  return false;
}

type HandleKind = 'point' | 'in' | 'out';

interface KnotHit {
  subpath: number;
  knot: number;
  handle: HandleKind;
}

/**
 * Finds what a Direct Selection click at `at` would grab. Each knot offers up
 * to three targets — its anchor and, when they are not coincident with it,
 * its two handles — and the closest one within `tolerance` wins. Ties (most
 * commonly a handle sitting exactly as close as its own anchor) favour the
 * handle, matching the common expectation that a visible handle is the more
 * specific, and therefore preferred, target.
 */
export function nearestKnot(
  subpaths: readonly Subpath[],
  at: Point,
  tolerance: number,
): KnotHit | null {
  let best: (KnotHit & { dist: number }) | null = null;

  for (let subpathIndex = 0; subpathIndex < subpaths.length; subpathIndex += 1) {
    const { knots } = subpaths[subpathIndex];
    for (let knotIndex = 0; knotIndex < knots.length; knotIndex += 1) {
      const k = knots[knotIndex];
      const candidates: { handle: HandleKind; pos: Point }[] = [{ handle: 'point', pos: k.point }];
      if (k.in.x !== 0 || k.in.y !== 0) {
        candidates.push({ handle: 'in', pos: { x: k.point.x + k.in.x, y: k.point.y + k.in.y } });
      }
      if (k.out.x !== 0 || k.out.y !== 0) {
        candidates.push({ handle: 'out', pos: { x: k.point.x + k.out.x, y: k.point.y + k.out.y } });
      }

      for (const candidate of candidates) {
        const dist = Math.hypot(at.x - candidate.pos.x, at.y - candidate.pos.y);
        if (dist > tolerance) continue;
        const isBetter =
          best === null ||
          dist < best.dist ||
          (dist === best.dist && best.handle === 'point' && candidate.handle !== 'point');
        if (isBetter) {
          best = { subpath: subpathIndex, knot: knotIndex, handle: candidate.handle, dist };
        }
      }
    }
  }

  return best === null ? null : { subpath: best.subpath, knot: best.knot, handle: best.handle };
}

/** Even-odd point-in-polygon (PNPOLY) test against one already-flattened, implicitly-closed ring. */
function windingContains(polyline: readonly Point[], at: Point): boolean {
  let inside = false;
  for (let i = 0, j = polyline.length - 1; i < polyline.length; j = i, i += 1) {
    const pi = polyline[i];
    const pj = polyline[j];
    const crosses = pi.y > at.y !== pj.y > at.y;
    if (crosses) {
      const xAtY = ((pj.x - pi.x) * (at.y - pi.y)) / (pj.y - pi.y) + pi.x;
      if (at.x < xAtY) inside = !inside;
    }
  }
  return inside;
}

/**
 * Even-odd containment over the flattened, closed subpaths, ignoring `op` —
 * callers combine multiple shapes' results themselves when `op` matters (e.g.
 * subtract/intersect between whole paths). Open subpaths have no fill region
 * of their own and are skipped, matching how a stroke-only open path never
 * contributes to a fill.
 */
export function pointInPath(subpaths: readonly Subpath[], at: Point): boolean {
  let inside = false;
  for (const subpath of subpaths) {
    if (!subpath.closed) continue;
    const polyline = subpathToPolyline(subpath);
    if (polyline.length < 3) continue;
    if (windingContains(polyline, at)) inside = !inside;
  }
  return inside;
}
